/**
 * Der Aufbau des Katalogs, wie ihn <c>/Calculator/edit/structure</c> liefert.
 *
 * Bewusst getrennt von der Anforderungsliste: die sagt, was es gibt, der Aufbau sagt, worauf es
 * steht. Zwei Fragen, zwei Verträge.
 */

/** Ein Unterbau, auf dem Bereiche aufsetzen. */
export interface PlatformDto {
    id: string;
    title: string;
    rank: number;
    /** Anforderungen, von denen EINE genügt, um den Unterbau bereitzustellen. */
    fulfilledBy: string[];
}

/** Ein Bereich des Katalogs mit dem, was er voraussetzt. */
export interface StructureGroupDto {
    path: string;
    title: string;
    /** Mehrere Einträge sind Alternativen, keine Summe. */
    requiredPlatforms: string[];
    /** Läuft auch ohne diesen Unterbau, dann aber mit Funktionseinschränkungen. */
    limitedWithout: string | null;
    einsatzarten: string[];
    /**
     * Die Anforderungen dieser Gruppe, direkt und ohne Untergruppen.
     *
     * Kommt vom Server mit, statt im Client über Namen erraten zu werden: `RequirementView.category`
     * trägt nur den Blattnamen („analog"), der Pfad hier den vollen („Funk.analog"). Über Namen
     * verglichen stand bei Funk und Bedienebene eine Null, obwohl beide voll besetzt sind.
     */
    requirementIds?: string[];
}

/** Was in einer Beispielkonfiguration feststeht. */
export interface TemplatePositionDto {
    requirementId: string;
    quantity: number;
    hint: string | null;
}

/**
 * Was üblich ist, aber nicht immer gewollt.
 *
 * Die dritte Kategorie: die Anforderung ist bekannt, die Frage lautet OB und nicht WELCHE. Ein
 * GSM-Gateway gehört in viele Einsatzleitwagen und in manche eben nicht.
 */
export interface TemplateOptionalPositionDto {
    requirementId: string;
    quantity: number;
    question: string;
    /** Beim Übernehmen gleich mitgesetzt — und trotzdem gefragt. */
    preselected: boolean;
}

/**
 * Was mengenmäßig feststeht, in der Auswahl aber nicht.
 *
 * Dass ein ELW 1 drei Funkanschaltungen hat, ist Erfahrung — ob Sepura, Motorola oder Betriebsfunk
 * daran hängt, weiß nur der Kunde.
 */
export interface TemplateOpenPositionDto {
    groupPath: string;
    quantity: number;
    question: string;
}

/** Eine Beispielkonfiguration für einen wiederkehrenden Fahrzeug- oder Anlagentyp. */
export interface ConfigurationTemplateDto {
    id: string;
    title: string;
    description: string | null;
    anlagenart: string | null;
    kundentyp?: string[];
    stichworte?: string[];
    /** Noch nicht fachlich bestätigt — beim Vorschlagen wird das mitgesagt. */
    isDraft: boolean;
    positions?: TemplatePositionDto[];
    optionalPositions?: TemplateOptionalPositionDto[];
    openPositions?: TemplateOpenPositionDto[];
}

/** Eine Art von Anlage, die das Haus baut — aus den Stammdaten, nicht aus einer Liste im Code. */
export interface AnlagenartDto {
    name: string;
    description: string | null;
}

export interface CatalogStructure {
    platforms: PlatformDto[];
    /** Fehlt bei älteren Server-Ständen. */
    anlagenarten?: AnlagenartDto[];
    groups: StructureGroupDto[];
    /** Fehlt bei älteren Server-Ständen. */
    templates?: ConfigurationTemplateDto[];
}

/**
 * Die Ids einer Gruppe, notfalls leer.
 *
 * Ein Server, der die Zuordnung noch nicht mitliefert, darf die Ansicht nicht zum Absturz bringen —
 * ein fehlendes Feld ist keine Ausnahme, sondern der Normalfall während eines Rollouts.
 */
export const idsOf = (g: StructureGroupDto): string[] => g.requirementIds ?? [];

/** Liefert der Aufbau überhaupt Zuordnungen? Sonst bleiben alle Bereiche leer. */
export const hasAssignments = (s: CatalogStructure): boolean =>
    s.groups.some((g) => idsOf(g).length > 0);
